import {
    type RefObject,
    useEffect,
    useLayoutEffect,
    useState,
    useSyncExternalStore,
} from "react";
import { observeOffScreen } from "./offScreenObserver";

const subscribe = (listener: () => void) => {
    document.addEventListener("visibilitychange", listener);
    return () => document.removeEventListener("visibilitychange", listener);
};

const getSnapshot = () => document.visibilityState === "hidden";
const getServerSnapshot = () => false;

/**
 * Keeps the CSS loops in `elementRef`'s subtree (pseudo-elements and animated
 * icons included) in one shared phase: every (re)start is pinned to the
 * document timeline's zero, so all instances show the same progress whatever
 * their duration, delay or mount time (the playground's slow motion
 * included). Returns `true` while the loops
 * should pause: the tab is hidden or the element is scrolled out of view.
 */
export const useSyncedAnimations = (elementRef: RefObject<Element | null>) => {
    const isTabHidden = useSyncExternalStore(
        subscribe,
        getSnapshot,
        getServerSnapshot,
    );
    const [isOffScreen, setIsOffScreen] = useState(false);
    useEffect(() => {
        const element = elementRef.current;
        return element ? observeOffScreen(element, setIsOffScreen) : undefined;
    }, [elementRef]);

    const isPaused = isTabHidden || isOffScreen;
    useLayoutEffect(() => {
        const element = elementRef.current;
        if (isPaused || !element || !("getAnimations" in element)) {
            return;
        }
        element.getAnimations({ subtree: true }).forEach((animation) => {
            animation.startTime = 0;
        });
    }, [elementRef, isPaused]);
    return isPaused;
};
